// ===== BEGIN FILE: src/components/report-v2/ReportIntroPage.tsx =====
/**
 * Report Introduction Page — appears right after the cover page.
 * Two parts:
 *   1. Philosophy: "Astrology: A Mirror of Karma and a Map to Meaningful Living"
 *   2. JP Morgan quote + Yogananda quote + Karma quote
 */

import React from 'react';
import { Box, Typography } from '@mui/joy';
import type { ReportData } from './types';

interface ReportIntroPageProps {
  data: ReportData;
}

const BRAND = {
  purple: '#5B21B6',
  purpleLight: '#7C3AED',
  purpleSoft: '#F5F3FF',
  gold: '#F59E0B',
  goldLight: '#FEF3C7',
  maroon: '#602941',
  text: '#1F2937',
  textMuted: '#6B7280',
  border: '#E5E7EB',
};

/** Decorative ornamental divider */
const OrnamentDivider: React.FC<{ color?: string }> = ({ color = BRAND.gold }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2.5 }}>
    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent, ${color})` }} />
    <Box component="span" sx={{ fontSize: '1rem', color, lineHeight: 1 }}>✦</Box>
    <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${color}, transparent)` }} />
  </Box>
);

/** Styled block-quote pull component */
const QuoteBlock: React.FC<{ quote: string; attribution?: string; accent?: string; bg?: string }> = ({
  quote,
  attribution,
  accent = BRAND.purple,
  bg = BRAND.purpleSoft,
}) => (
  <Box
    sx={{
      position: 'relative',
      background: bg,
      borderLeft: `4px solid ${accent}`,
      borderRadius: '0 12px 12px 0',
      px: 3,
      py: 2,
      my: 2,
    }}
  >
    {/* Large decorative open-quote */}
    <Box
      component="span"
      sx={{
        position: 'absolute',
        top: '-8px',
        left: '12px',
        fontSize: '3rem',
        lineHeight: 1,
        color: accent,
        opacity: 0.25,
        fontFamily: 'Georgia, serif',
        userSelect: 'none',
      }}
    >
      "
    </Box>
    <Typography
      sx={{
        fontStyle: 'italic',
        fontSize: '0.92rem',
        lineHeight: 1.75,
        color: BRAND.text,
        fontFamily: 'Georgia, serif',
        pl: 1,
      }}
    >
      {quote}
    </Typography>
    {attribution && (
      <Typography
        sx={{
          mt: 0.75,
          textAlign: 'right',
          fontSize: '0.78rem',
          fontWeight: 600,
          color: accent,
          letterSpacing: '0.02em',
        }}
      >
        — {attribution}
      </Typography>
    )}
  </Box>
);

export const ReportIntroPage: React.FC<ReportIntroPageProps> = ({ data }) => {
  return (
    <Box
      className="report-section"
      sx={{
        background: '#FFFFFF',
        minHeight: '297mm',        // A4 height
        p: '14mm 12mm 12mm',
        // NOTE: no breakBefore here — the .report-section CSS class handles the page break.
        // Duplicating it in sx caused a double break → blank page.
        '@media print': {
          minHeight: 'unset',  // removing 297mm forces real content height, avoids blank overflow page
          p: '14mm 12mm 12mm',
        },
      }}
    >
      {/* ── Page banner header ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND.purple} 0%, #4C1D95 50%, ${BRAND.maroon} 100%)`,
          borderRadius: '12px',
          px: 3,
          py: 2,
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          '@media print': {
            background: BRAND.purple,   // flat color — gradients band in PDF
            borderRadius: '6px',
          },
        }}
      >
        <Box component="span" sx={{ fontSize: '1.8rem', flexShrink: 0 }}>🪷</Box>
        <Box>
          <Typography
            sx={{
              color: '#FFF',
              fontSize: '1.05rem',
              fontWeight: 700,
              letterSpacing: '0.03em',
              lineHeight: 1.3,
            }}
          >
            Astrology: A Mirror of Karma &amp; a Map to Meaningful Living
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.75rem', mt: 0.25 }}>
            A note before you read your report, {data.birthDetails.name.split(' ')[0]}
          </Typography>
        </Box>
      </Box>

      {/* ── JP Morgan quote — the attention anchor ── */}
      <QuoteBlock
        quote="Millionaires don't use astrology; billionaires do."
        attribution="J.P. Morgan"
        accent={BRAND.gold}
        bg={BRAND.goldLight}
      />

      <OrnamentDivider />

      {/* ── Opening paragraph ── */}
      <Typography
        sx={{
          fontSize: '0.88rem',
          lineHeight: 1.8,
          color: BRAND.text,
          mb: 1.5,
        }}
      >
        Your birth chart is not just a snapshot of stars — it is the imprint of your past-life karmas. In Vedic
        philosophy, astrology reflects the results of actions from previous lives, showing both your strengths and
        the challenges you carry. Every planet, every house, every yoga in your chart is a chapter of the story
        your soul has written across lifetimes.
      </Typography>

      <Typography
        sx={{
          fontSize: '0.88rem',
          lineHeight: 1.8,
          color: BRAND.text,
          mb: 2,
        }}
      >
        The chart does not imprison you — it illuminates you. Understanding its patterns is the first step to
        consciously rewriting them.
      </Typography>

      {/* ── Yogananda quote ── */}
      <QuoteBlock
        quote="The message boldly blazoned across the heavens at the moment of birth is not meant to emphasize fate — but to arouse our will to act."
        attribution="Paramahansa Yogananda"
        accent={BRAND.purple}
        bg={BRAND.purpleSoft}
      />

      {/* ── Karma explanation ── */}
      <Typography
        sx={{
          fontSize: '0.88rem',
          lineHeight: 1.8,
          color: BRAND.text,
          mt: 1.5,
          mb: 1.5,
        }}
      >
        Astrology's purpose is not to bind but to <strong>awaken</strong> — to make you aware of patterns you
        carry and offer remedies and direction to reduce negative baggage and create better karma. When we
        understand our tendencies, we gain the power to transcend them.
      </Typography>

      {/* ── Karma quote ── */}
      <QuoteBlock
        quote="Karma is not fate — it is your unconscious patterns. Astrology makes them conscious."
        accent="#047857"
        bg="#ECFDF5"
      />

      {/* ── Closing philosophy ── */}
      <Typography
        sx={{
          fontSize: '0.88rem',
          lineHeight: 1.8,
          color: BRAND.text,
          mt: 1.5,
          mb: 1,
        }}
      >
        When used wisely, astrology becomes your{' '}
        <Box component="span" sx={{ fontWeight: 700, color: BRAND.purple }}>
          spiritual compass
        </Box>{' '}
        — guiding you to live more meaningfully, purposefully, and peacefully. It helps you navigate life's
        opportunities with confidence and its challenges with grace.
      </Typography>

      <OrnamentDivider color={BRAND.purple} />

      {/* ── Footer note for this page ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND.purpleSoft}, #FFF7ED)`,
          border: `1px solid ${BRAND.border}`,
          borderRadius: '10px',
          p: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box component="span" sx={{ fontSize: '1.4rem', flexShrink: 0, mt: 0.25 }}>📖</Box>
        <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.7, color: BRAND.textMuted }}>
          The pages that follow are your personal Vedic astrology analysis — a synthesis of classical Sanskrit
          wisdom and modern computation. Read slowly, reflect deeply, and use it as a living guide rather than a
          fixed prophecy. Turn to the <strong>Index page</strong> next for a structured overview of all sections.
        </Typography>
      </Box>
    </Box>
  );
};
